import { fx } from '../ui/motion-control.js';
import { breakpoint } from '../utils/variables.js';

class HeaderNavigation {
    // --- Constructor ---
    constructor() {
        this.headerNavigation = document.querySelector('.header-navigation');
        this.headerCTA = this.headerNavigation?.querySelector('.cta') || null;
        this.headerMenu = document.querySelector('.header-menu');
        this.headerActions = document.querySelector('.header-actions');
        this.headerSocials = document.querySelector('.header-socials');

        // Header Menu elements
        this.menuItems = this.headerMenu?.querySelectorAll('.menu-item') || [];
        this.menuLinks = this.headerMenu?.querySelectorAll('.menu-link') || [];
        this.menuTriggers = this.headerMenu?.querySelectorAll('.menu-item-has-children > a') || [];
        this.megaMenus = this.headerMenu?.querySelectorAll('.sub-menu-level-0') || [];
        this.subMenus = this.headerMenu?.querySelectorAll('.sub-menu') || [];

        /* Si les actions sont séparés par des boutons différents */
        // this.mobileMenuOpenBtn = document.querySelector('[data-js="open-mobile-menu"]');
        // this.mobileMenuCloseBtn = document.querySelector('[data-js="close-mobile-menu"]');

        this.header = document.querySelector('header');
        this.visibleItems = [];

        // flag to track the previous breakpoint width
        this.previousBreakpointWidth = window.innerWidth;
        this.previousMenuTrigger = null;
    }

    init() {
        if (!this.headerMenu) return;

        this.syncAccessibilityState();

        if (this.menuTriggers.length > 0) {
            this.menuTriggers.forEach((menuTrigger) => {
                // Initialize aria-expanded to false for all menuTriggers
                menuTrigger.setAttribute('aria-expanded', 'false');

                // Once header navigation is loaded, disable lazy chunk triggers on menu links
                if (menuTrigger.hasAttribute('data-chunk')) {
                    menuTrigger.removeAttribute('data-chunk');
                    menuTrigger.removeAttribute('data-chunk-lazy');
                    menuTrigger.removeAttribute('data-chunk-preload');
                }
            });
        }

        // Delegated click for submenu triggers (works for all levels)
        this.headerMenu.addEventListener('click', (event) => {
            const menuTrigger = event.target.closest('.menu-item-has-children > a');
            if (!menuTrigger || !this.headerMenu.contains(menuTrigger)) return;
            this.handleMenuLinkClick({
                preventDefault: () => event.preventDefault(),
                currentTarget: menuTrigger,
            });
        });

        // Keydown event listener
        document.addEventListener('keydown', this.handleEscapeKey.bind(this));

        // Close desktop menu on outside click
        document.addEventListener('click', (event) => {
            if (
                this.isDesktopNavigation() &&
                this.previousMenuTrigger !== null &&
                !this.headerNavigation?.contains(event.target)
            ) {
                this.closeDesktopMenu();
            }
        });

        // Add event listener for window resize
        window.addEventListener('resizeEnd', this.handleResizeEnd.bind(this));
    }

    handleOpenMobileMenuClick(event) {
        event.preventDefault();

        // Set aria-expanded="true" on the opened menuTrigger
        event.currentTarget.setAttribute('aria-expanded', true);
        this.mobileMenuCloseBtn?.setAttribute('aria-expanded', true);

        // Open the mobile menu
        this.openMobileMenu();
    }

    handleCloseMobileMenuClick(event) {
        event.preventDefault();

        // Set aria-expanded="false" on the closed menuTrigger
        event.currentTarget.setAttribute('aria-expanded', false);
        this.mobileMenuOpenBtn?.setAttribute('aria-expanded', false);

        // Close the mobile menu
        this.closeMobileMenu();
    }

    handleMenuLinkClick(event) {
        event.preventDefault();

        // Use currentTarget to ensure the anchor itself is targeted (even if a child element was clicked)
        const menuTrigger = event.currentTarget;
        const isExpanded = menuTrigger.getAttribute('aria-expanded') === 'true';

        if (this.isDesktopNavigation()) {
            if (this.previousMenuTrigger == menuTrigger && isExpanded)
                return this.closeDesktopMenu();

            return this.openDesktopSubmenu(menuTrigger);
        }

        if (this.isMobileNavigation()) {
            if (isExpanded) {
                return this.closeMobileSubmenu(menuTrigger);
            }

            return this.openMobileSubmenu(menuTrigger);
        }
    }

    handleBreadcrumbClick(event) {
        event.preventDefault();

        const clickedLink = event.currentTarget;
        const label = clickedLink.textContent.trim();

        const allLinks = this.headerMenu.querySelectorAll('.menu-link');

        let matchedLink = null;

        allLinks.forEach((link) => {
            if (link.textContent.trim() === label) {
                matchedLink = link;
            }
        });

        if (!matchedLink) return;

        const subMenu = matchedLink.nextElementSibling;
        const subMenuLevel0 = clickedLink.closest('.sub-menu-level-0');

        // Set aria-expanded="true" on matchedLink
        this.updateAriaExpanded(matchedLink, true);

        // Set aria-expanded="false" on all other submenuTriggers
        const submenuTriggers = subMenu.querySelectorAll('.menu-item-has-children > a');
        if (submenuTriggers.length > 0) {
            submenuTriggers.forEach((trigger) => {
                this.updateAriaExpanded(trigger, false);
            });
        }

        // Cache la liste actuellement affichée (frères de matchedLink), sauf si ce dernier
        // est du premier niveau (dans ce cas ce sont les autres items du menu principal).
        // Le <li> de matchedLink reste dans le DOM (son sous-menu doit s'afficher), seul
        // son propre lien est masqué (son contenu est repris par le titre).
        if (!matchedLink.classList.contains('menu-link-level-0')) {
            const currentList = matchedLink.closest('ul');
            const currentItem = matchedLink.parentElement;
            if (currentList) {
                const itemsToHide = Array.from(currentList.children).filter(
                    (child) =>
                        child !== currentItem &&
                        !child.classList.contains('menu-breadcrumb') &&
                        !child.classList.contains('menu-title')
                );
                fx.set(itemsToHide, { display: 'none' });
            }
            fx.set(matchedLink, { display: 'none' });
            currentItem?.classList.add('menu-item-open');
        }

        // Réaffiche le sous-menu de matchedLink
        fx.set(subMenu, { display: 'flex' });
        this.animateDesktopItems(subMenu.children);

        // Cache les sous-menus internes et réinitialise les niveaux plus profonds
        const nestedSubmenus = subMenu.querySelectorAll('.sub-menu');
        const nestedMenuItems = subMenu.querySelectorAll('.menu-item');
        // les liens eux-mêmes peuvent avoir été masqués individuellement (devenus titre) :
        // il faut aussi les réafficher, pas seulement leur <li>
        const nestedMenuLinks = subMenu.querySelectorAll('.menu-link');

        // reset les display précédents via gsap
        fx.set(nestedSubmenus, { display: 'none' });
        fx.set(nestedMenuItems, fx.clearDisplay);
        fx.set(nestedMenuLinks, fx.clearDisplay);

        nestedMenuItems.forEach((item) => item.classList.remove('menu-item-open'));

        // Met à jour le titre affiché sous le fil d'ariane avec le contenu de matchedLink
        this.updateSubmenuTitle(subMenuLevel0, matchedLink);

        // mise à jour de la hauteur du sous-menu
        this.updateSubmenuHeight(subMenuLevel0);

        // Supprime les boutons breadcrumb suivants
        const breadcrumbNav = clickedLink.parentElement;
        const breadcrumbButtons = [...breadcrumbNav.children];

        // supprimer le event.preventDefault() du dernier bouton du breadcrumb
        if (clickedLink === breadcrumbButtons[breadcrumbButtons.length - 1]) {
            window.location.href = clickedLink.getAttribute('href');
            return;
        }

        let found = false;
        breadcrumbButtons.forEach((btn) => {
            if (btn === clickedLink) {
                found = true;
            } else if (found) {
                btn.remove();
            }
        });
    }

    handleResizeEnd() {
        const currentWidth = window.innerWidth;
        const prevIsDesktop = this.previousBreakpointWidth >= breakpoint.sidebar;
        const currIsDesktop = currentWidth >= breakpoint.sidebar;

        if (prevIsDesktop !== currIsDesktop) {
            this.resetNavigationState();
        }

        this.syncAccessibilityState();

        // Toujours garder une trace à jour
        this.previousBreakpointWidth = currentWidth;
    }

    handleEscapeKey(event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
            if (this.isDesktopNavigation()) {
                return this.closeDesktopMenu();
            }

            if (this.isMobileNavigation()) {
                return this.closeMobileMenu();
            }
        }
    }

    /* --- Mobile methods --- */
    openMobileMenu() {
        if (!this.headerMenu) return;

        this.header?.classList.add('is-open');
        document.documentElement.classList.add('is-scroll-locked');
        this.setNavigationAccessibility(false);

        const menuLinksLevel0 = this.headerMenu.querySelectorAll('.menu-link-level-0');
        const hasSubmenuExpanded = this.headerMenu.querySelector(
            '.menu-link-level-0[aria-expanded="true"]'
        );

        fx.set(this.headerMenu, { display: 'flex' });
        fx.set(this.headerSocials, { display: 'flex' });
        fx.fromTo(
            this.headerSocials,
            { yPercent: 100 },
            { yPercent: 0, duration: 0.5, ease: 'power2.out' }
        );

        const tl = fx.timeline();

        // Animation d'apparition du CTA "Mes Démarches"
        tl.to(this.headerCTA, {
            yPercent: -100,
            opacity: 0,
            duration: 0.5,
            ease: 'back.in(1.7)',
            display: 'none',
        });

        // Animation d'apparition du bouton de fermeture
        tl.fromTo(
            this.mobileMenuCloseBtn,
            { opacity: 0, scale: 0 },
            { display: 'inline-flex', opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(1.7)' }
        );

        if (hasSubmenuExpanded) return;

        fx.fromTo(
            menuLinksLevel0,
            { opacity: 0, yPercent: 100 },
            {
                opacity: 1,
                yPercent: 0,
                duration: 0.5,
                stagger: 0.05,
                ease: 'back.out(1.7)',
                onComplete: () => fx.set(menuLinksLevel0, fx.clearProps),
            }
        );
    }

    closeMobileMenu() {
        if (!this.headerMenu) return;

        this.header?.classList.remove('is-open');
        document.documentElement.classList.remove('is-scroll-locked');
        fx.set(this.headerMenu, { display: 'none' });
        fx.set(this.headerSocials, { display: 'none' });

        this.resetMobileSubmenus();

        this.updateAriaExpanded(this.mobileMenuOpenBtn, false);
        this.updateAriaExpanded(this.mobileMenuCloseBtn, false);

        const tl = fx.timeline();

        // Animation de disparition du bouton de fermeture
        tl.to(this.mobileMenuCloseBtn, {
            opacity: 0,
            scale: 0,
            duration: 0.5,
            ease: 'back.in(1.7)',
            display: 'none',
        });

        // Animation de disparition du CTA "Mes Démarches"
        tl.set(this.headerCTA, { display: 'inline-flex' });
        tl.to(this.headerCTA, {
            yPercent: 0,
            opacity: 1,
            duration: 0.5,
            ease: 'back.out(1.7)',
        });

        this.setNavigationAccessibility(true);
    }

    openMobileSubmenu(menuTrigger) {
        const subMenu = menuTrigger.nextElementSibling;
        if (!subMenu) return;

        const parentList = menuTrigger.closest('ul');
        const parentItem = menuTrigger.parentElement;
        const parentTrigger = parentList?.previousElementSibling;
        const isLevel0 = menuTrigger.classList.contains('menu-link-level-0');

        // hide the parent list items to "replace" the viewport with the child list
        if (parentList) {
            Array.from(parentList.children).forEach((item) => {
                if (item !== parentItem) {
                    item.classList.add('is-mobile-hidden');
                }
            });
        }
        menuTrigger.classList.add('is-mobile-back');
        menuTrigger.classList.add('is-mobile-title');

        // If opening a level-1 submenu, hide the level-0 title
        if (!isLevel0 && parentTrigger?.classList.contains('menu-link-level-0')) {
            parentTrigger.classList.add('is-mobile-hidden');
        }

        // show current submenu only
        subMenu.style.display = 'block';
        this.animateMobileItems(subMenu);

        // aria-expanded: current true, siblings false
        if (parentList) {
            const siblingTriggers = parentList.querySelectorAll('.menu-item-has-children > a');
            siblingTriggers.forEach((trigger) => {
                this.updateAriaExpanded(trigger, trigger === menuTrigger);
            });
        } else {
            this.updateAriaExpanded(menuTrigger, true);
        }
    }

    closeMobileSubmenu(menuTrigger) {
        const subMenu = menuTrigger.nextElementSibling;
        if (!subMenu) return;

        const parentList = menuTrigger.closest('ul');
        const parentTrigger = parentList?.previousElementSibling;
        const isLevel0 = menuTrigger.classList.contains('menu-link-level-0');
        // restore parent list items
        if (parentList) {
            Array.from(parentList.children).forEach((item) => {
                item.classList.remove('is-mobile-hidden');
            });
        }
        menuTrigger.classList.remove('is-mobile-hidden');

        menuTrigger.classList.remove('is-mobile-back');
        menuTrigger.classList.remove('is-mobile-title');

        if (!isLevel0 && parentTrigger?.classList.contains('menu-link-level-0')) {
            parentTrigger.classList.remove('is-mobile-hidden');
        }

        subMenu.style.display = 'none';
        this.updateAriaExpanded(menuTrigger, false);
    }

    animateMobileItems(subMenu) {
        const siblingLink = subMenu.previousElementSibling;

        if (siblingLink.getAttribute('aria-expanded') === 'true') return;

        fx.set(subMenu.children, { opacity: 0, x: -50 });
        fx.to(subMenu.children, {
            opacity: 1,
            x: 0,
            duration: 0.5,
            ease: 'back.out(1.7)',
            stagger: 0.05,
            onComplete: () => fx.set(subMenu.children, fx.clearProps),
        });
    }

    resetMobileSubmenus() {
        if (!this.headerMenu) return;

        this.headerMenu.querySelectorAll('.is-mobile-hidden').forEach((item) => {
            item.classList.remove('is-mobile-hidden');
        });

        this.subMenus.forEach((subMenu) => {
            subMenu.style.display = 'none';
        });

        this.menuTriggers.forEach((menuTrigger) => {
            this.updateAriaExpanded(menuTrigger, false);
            menuTrigger.classList.remove('is-mobile-back');
        });
    }

    getMobileVisibleItems() {
        const menuLinksExpanded = this.headerMenu.querySelectorAll('[aria-expanded="true"]');
        const menuLinksLevel0 = this.headerMenu.querySelectorAll('.menu-link-level-0');

        if (!menuLinksExpanded.length) return menuLinksLevel0;

        const visibleItems = [];

        menuLinksExpanded.forEach((link) => {
            const siblingList = link.nextElementSibling;
            const siblingChildren = Array.from(siblingList.children);

            siblingChildren.forEach((child) => {
                visibleItems.push(child.querySelector('.menu-link'));
            });
        });

        return visibleItems;
    }

    /* --- Desktop methods --- */
    closeDesktopMenu() {
        if (!this.megaMenus) return;
        this.megaMenus.forEach((subMenuLevel0) => {
            this.closeDesktopSubmenu(subMenuLevel0);
        });
        this.header?.classList.remove('is-open');
        this.previousMenuTrigger = null;
    }

    closeDesktopSubmenu(subMenuLevel0) {
        fx.set(subMenuLevel0, { display: 'none' });

        this.headerNavigation.style.setProperty('--submenu-height', '0px');
        this.headerNavigation.removeAttribute('data-background-expanded');

        // Find menuTrigger inside subMenuLevel0 with aria-expanded="true" and set to false
        const expandedTrigger = subMenuLevel0.parentElement.querySelector('[aria-expanded="true"]');
        this.updateAriaExpanded(expandedTrigger, false);
    }

    openDesktopSubmenu(menuTrigger) {
        window.headerSearch?.close?.(false);

        // sous-menu courant (frère de menuTrigger)
        const subMenu = menuTrigger.nextElementSibling;
        if (!subMenu) return;

        const wasExpanded = menuTrigger.getAttribute('aria-expanded') === 'true';
        const isTopLevel = menuTrigger.classList.contains('menu-link-level-0');
        const subMenuLevel0 = subMenu.classList.contains('sub-menu-level-0')
            ? subMenu
            : subMenu.closest('.sub-menu-level-0');

        // niveau 0: reset complet
        if (isTopLevel) {
            this.resetNavigationState();
        } else if (subMenuLevel0) {
            // niveau 1+: garder le sub-menu-level-0 visible, masquer les autres sous-menus déjà ouverts
            const level1Submenus = subMenuLevel0.querySelectorAll('.sub-menu-level-1');
            level1Submenus.forEach((submenuEl) => {
                if (submenuEl !== subMenu) {
                    fx.set(submenuEl, { display: 'none' });
                }
            });

            // met à jour les aria-expanded des triggers du niveau courant
            const submenuTriggers = subMenuLevel0.querySelectorAll('.menu-item-has-children > a');
            submenuTriggers.forEach((trigger) => {
                this.updateAriaExpanded(trigger, trigger === menuTrigger);
            });

            // Cache la liste actuellement affichée (celle contenant menuTrigger) : elle est
            // remplacée par les enfants de menuTrigger, dont le contenu devient le titre.
            // Le <li> de menuTrigger reste dans le DOM (son sous-menu doit s'afficher),
            // seul son propre lien est masqué (son contenu est repris par le titre).
            const currentList = menuTrigger.closest('ul');
            const currentItem = menuTrigger.parentElement;
            if (currentList) {
                const itemsToHide = Array.from(currentList.children).filter(
                    (child) =>
                        child !== currentItem &&
                        !child.classList.contains('menu-breadcrumb') &&
                        !child.classList.contains('menu-title')
                );
                fx.set(itemsToHide, { display: 'none' });
            }
            fx.set(menuTrigger, { display: 'none' });
            currentItem?.classList.add('menu-item-open');
        }

        // show current submenu
        fx.set(subMenu, { display: 'flex' });

        // animate current submenu items
        if (!wasExpanded) {
            this.animateDesktopItems(subMenu.children);
        }

        // Set aria-expanded true on the opened menuTrigger
        this.updateAriaExpanded(menuTrigger, true);

        // Ensure breadcrumb structure + titre affiché sous le fil d'ariane
        const breadcrumb = this.ensureBreadcrumbStructure(subMenuLevel0);
        this.addBreadcrumbButton(menuTrigger, breadcrumb);
        this.updateSubmenuTitle(subMenuLevel0, menuTrigger);
        this.updateSubmenuHeight(subMenuLevel0);

        this.header?.classList.add('is-open');

        // update previous menu trigger
        this.previousMenuTrigger = menuTrigger;
    }

    animateDesktopItems(items) {
        items = Array.from(items).filter((item) => item.classList.contains('menu-item'));

        fx.set(items, { opacity: 0, y: 50 });
        fx.to(items, {
            opacity: 1,
            y: 0,
            duration: 0.5,
            stagger: 0.05,
            ease: 'back.out(1.7)',
            onComplete: () => {
                fx.set(items, fx.clearProps);
            },
        });
    }

    updateSubmenuHeight(subMenuLevel0) {
        if (!this.headerNavigation || !subMenuLevel0) return;
        this.headerNavigation.style.setProperty(
            '--submenu-height', '363px'
            // `${subMenuLevel0.offsetHeight}px`
        );
        this.headerNavigation.setAttribute('data-background-expanded', 'true');
    }

    updateSubmenuTitle(subMenuLevel0, menuTrigger) {
        if (!subMenuLevel0 || !menuTrigger) return;

        let titleItem = subMenuLevel0.querySelector(':scope > .menu-title');
        let title;

        if (!titleItem) {
            titleItem = document.createElement('li');
            titleItem.classList.add('menu-item', 'menu-item-open', 'menu-title');

            title = document.createElement('a');
            title.classList.add('menu-link', 'menu-link-level-1', 'is-mobile-title');
            titleItem.appendChild(title);

            // Le titre doit être inséré avant les enfants (juste après le fil d'ariane s'il existe)
            const breadcrumb = subMenuLevel0.querySelector(':scope > .menu-breadcrumb');
            if (breadcrumb) {
                breadcrumb.insertAdjacentElement('afterend', titleItem);
            } else {
                subMenuLevel0.insertBefore(titleItem, subMenuLevel0.firstChild);
            }
        } else {
            title = titleItem.querySelector('.menu-link');
        }

        // S'assure que les classes sont bien présentes même si un nettoyage externe
        // (ex: fermeture d'un sous-menu enfant) les a retirées entre-temps
        titleItem.classList.add('menu-item', 'menu-item-open', 'menu-title');
        title.classList.add('menu-link', 'menu-link-level-1', 'is-mobile-title');

        title.textContent = menuTrigger.textContent.trim();
        title.setAttribute('href', menuTrigger.getAttribute('href') || '#');
        title.setAttribute('aria-expanded', 'true');
    }

    ensureBreadcrumbStructure(subMenuLevel0) {
        let breadcrumb = subMenuLevel0.querySelector('.menu-breadcrumb');
        let closeBtn = subMenuLevel0.querySelector('.menu-close');

        if (!closeBtn) {
            closeBtn = document.createElement('button');
            closeBtn.classList.add('menu-close');
            closeBtn.type = 'button';
            closeBtn.textContent = 'Fermer';

            closeBtn.addEventListener('click', () => {
                this.closeDesktopSubmenu(subMenuLevel0);
            });
        }

        if (!breadcrumb) {
            let nav = document.createElement('nav');

            // ajout des aria-label et aria-live
            nav.setAttribute('aria-label', 'Fil d’Ariane');
            nav.setAttribute('aria-live', 'polite');

            breadcrumb = document.createElement('li');
            // ajout de la classe menu-breadcrumb pour le dissocier des .menu-item
            breadcrumb.classList.add('menu-breadcrumb');
            // ajout du bouton close dans le breadcrumb
            breadcrumb.appendChild(nav);
            // ajout de la nav dans le breadcrumb
            breadcrumb.appendChild(closeBtn);
            // le fil d'ariane doit être le tout premier élément de .sub-menu-level-0
            // (ordre attendu : 1. breadcrumb, 2. titre, 3. reste des items)
            subMenuLevel0.insertBefore(breadcrumb, subMenuLevel0.firstChild);
        }

        return breadcrumb;
    }

    addBreadcrumbButton(menuTrigger, breadcrumb) {
        const existingButton = [...breadcrumb.querySelectorAll('.menu-breadcrumb-button')].find(
            (btn) => btn?.textContent?.trim() === menuTrigger.textContent.trim()
        );

        if (!existingButton) {
            const backLink = document.createElement('a');
            backLink.textContent = menuTrigger.textContent;
            backLink.href = menuTrigger.getAttribute('href');
            backLink.classList.add('menu-breadcrumb-button');

            const breadcrumbNav = breadcrumb.querySelector('nav');
            breadcrumbNav.appendChild(backLink);

            // Vérifie si le lien correspond à l’URL actuelle
            const currentUrl = window.location.href;
            if (backLink.href === currentUrl || backLink.href === currentUrl.replace(/\/$/, '')) {
                backLink.setAttribute('aria-current', 'page');
            }

            // Ajout du nouvel event listener unique
            backLink.addEventListener('click', this.handleBreadcrumbClick.bind(this));
        }
    }

    /* --- Utils --- */
    shouldHandleSubmenu(menuTrigger) {
        return !menuTrigger.classList.contains('menu-link-level-0');
    }

    updateAriaExpanded(menuTrigger, isExpanded) {
        if (!menuTrigger) return;
        menuTrigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        menuTrigger.ariaExpanded = isExpanded ? true : false;
    }

    setNavigationAccessibility(isHidden) {
        if (!this.headerNavigation) return;

        this.headerNavigation.setAttribute('aria-hidden', isHidden ? 'true' : 'false');

        if (isHidden) {
            this.headerNavigation.setAttribute('inert', '');
            return;
        }

        this.headerNavigation.removeAttribute('inert');
    }

    syncAccessibilityState() {
        this.setNavigationAccessibility(this.isMobileNavigation());
    }

    resetNavigationState() {
        // Réinitialise aussi les display au besoin
        fx.set(this.headerMenu, fx.clearDisplay);
        fx.set(this.menuItems, fx.clearDisplay);
        fx.set(this.menuLinks, fx.clearDisplay);
        fx.set(this.subMenus, fx.clearDisplay);
        fx.set(this.mobileMenuCloseBtn, fx.clearDisplay);

        // Réinitialise aussi les props au besoin
        fx.set(this.headerCTA, fx.clearProps);
        fx.set(this.headerSocials, fx.clearProps);

        // Réinitialise aussi les aria-expanded au besoin
        this.updateAriaExpanded(this.mobileMenuOpenBtn, false);
        this.menuTriggers.forEach((menuTrigger) => {
            this.updateAriaExpanded(menuTrigger, false);
        });

        // Réinitialise aussi les classes au besoin
        this.menuItems.forEach((menuItem) => {
            menuItem.classList.remove('menu-item-open');
        });
        this.menuLinks.forEach((menuLink) => {
            menuLink.classList.remove('is-mobile-title');
        });

        // Supprime tous les .menu-breadcrumb
        this.headerMenu.querySelectorAll('.menu-breadcrumb').forEach((breadcrumb) => {
            breadcrumb.remove();
        });

        // Supprime tous les titres injectés sous le fil d'ariane
        this.headerMenu.querySelectorAll('.menu-title').forEach((titleItem) => {
            titleItem.remove();
        });

        this.headerNavigation.removeAttribute('data-background-expanded');
        this.headerNavigation.style.removeProperty('--submenu-height');
        this.previousMenuTrigger = null;
        this.syncAccessibilityState();
    }

    isDesktopNavigation() {
        return window.innerWidth >= breakpoint.sidebar;
    }

    isMobileNavigation() {
        return window.innerWidth < breakpoint.sidebar;
    }
}

export default {
    init() {
        this.instance = new HeaderNavigation();
        this.instance.init();
        window.headerNavigation = this.instance;
    },
    resetNavigation() {
        this.instance?.resetNavigationState();
    },
};
